import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";

const input =
  "w-full bg-[#050505] px-4 py-3 text-sm text-white outline-none ring-1 ring-zinc-800 transition-shadow focus:ring-[#00ffcc]";
const label = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("This reset link is missing its token. Request a new one from the forgot-password page.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      navigate("/login", { replace: true, state: { resetOk: true } });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="mx-auto max-w-md px-5 py-20 lg:py-28">
      <h1 className="font-display text-3xl tracking-tighter">Choose a new password</h1>
      <p className="mt-3 text-xs text-zinc-500">
        This link works once and expires 10 minutes after it was sent.
      </p>
      <form onSubmit={submit} className="mt-10 space-y-6 border border-[#1f1f1f] bg-[#0a0a0a] p-6">
        <div>
          <label className={label}>New password</label>
          <input
            data-testid="reset-password-input"
            className={input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className={label}>Confirm password</label>
          <input
            data-testid="reset-password-confirm"
            className={input}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        {error && (
          <p
            data-testid="reset-password-error"
            className="border border-[#ff3b30] bg-[#ff3b30]/10 p-3 text-xs text-[#ff3b30]"
          >
            {error}
          </p>
        )}
        <button
          data-testid="reset-password-submit"
          disabled={busy || !token}
          className="w-full border border-[#00ffcc] py-3 text-[11px] uppercase tracking-[0.3em] text-[#00ffcc] transition-colors hover:bg-[#00ffcc] hover:text-black disabled:opacity-50"
        >
          {busy ? "Updating…" : "Update password"}
        </button>
        <p className="text-xs text-zinc-500">
          <Link to="/forgot-password" className="text-[#00ffcc] hover:underline">
            Request a new link
          </Link>
          {" · "}
          <Link to="/login" className="text-[#00ffcc] hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
